import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import { Building2, Users, FolderOpen, CheckCircle, ArrowRight, Plus } from "lucide-react";
import { Badge } from "@/components/ui/badge";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const STATUS_COLORS = {
  neu: "bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-400",
  kontaktiert: "bg-yellow-100 text-yellow-700 dark:bg-yellow-900/30 dark:text-yellow-400",
  konvertiert: "bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-400",
  verloren: "bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-400",
};

export default function DashboardPage() {
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    axios.get(`${API}/dashboard/stats`, { withCredentials: true })
      .then(res => setStats(res.data))
      .catch(err => { if (err.response?.status !== 401) console.error(err); })
      .finally(() => setLoading(false));
  }, []);

  if (loading) return <div className="flex items-center justify-center h-64 text-muted-foreground text-sm">Laden...</div>;

  const cards = [
    { label: "Firmen gesamt", value: stats?.total_companies ?? 0, icon: Building2, color: "text-blue-600", to: "/firmen" },
    { label: "Aktive Leads", value: stats?.active_leads ?? 0, icon: Users, color: "text-amber-600", to: "/leads" },
    { label: "Projekte aktiv", value: stats?.projects_active ?? 0, icon: FolderOpen, color: "text-purple-600", to: "/firmen" },
    { label: "Abgeschlossen", value: stats?.completed_projects ?? 0, icon: CheckCircle, color: "text-green-600", to: "/firmen" },
  ];

  return (
    <div className="space-y-6 max-w-7xl">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <p className="text-xs uppercase tracking-widest text-muted-foreground font-medium mb-0.5">Übersicht</p>
          <h1 className="font-heading text-2xl font-black tracking-tight text-foreground">Dashboard</h1>
        </div>
        <div className="flex gap-2">
          <Link to="/firmen" data-testid="dashboard-new-company-btn" className="flex items-center gap-1.5 px-3 py-2 bg-primary text-primary-foreground text-xs font-medium rounded-sm hover:bg-primary/90 transition-colors">
            <Plus size={13} /> Neue Firma
          </Link>
          <Link to="/leads" data-testid="dashboard-new-lead-btn" className="flex items-center gap-1.5 px-3 py-2 bg-muted text-foreground text-xs font-medium rounded-sm hover:bg-muted/70 transition-colors border border-border">
            <Plus size={13} /> Neuer Lead
          </Link>
        </div>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        {cards.map(({ label, value, icon: Icon, color, to }) => (
          <Link
            key={label}
            to={to}
            data-testid={`stat-card-${label.toLowerCase().replace(/\s/g, "-")}`}
            className="bg-card border border-border p-5 rounded-sm hover:-translate-y-0.5 hover:border-foreground/20 transition-all duration-200"
          >
            <div className="flex items-start justify-between mb-3">
              <Icon size={16} className={color} />
              <ArrowRight size={12} className="text-muted-foreground" />
            </div>
            <div className="font-mono text-3xl font-bold text-foreground">{value}</div>
            <div className="text-xs text-muted-foreground mt-1">{label}</div>
          </Link>
        ))}
      </div>

      {/* Recent Activity */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        {/* Recent Companies */}
        <div className="bg-card border border-border rounded-sm">
          <div className="flex items-center justify-between px-5 py-3.5 border-b border-border">
            <span className="text-xs font-semibold uppercase tracking-widest text-muted-foreground">Neueste Firmen</span>
            <Link to="/firmen" className="text-xs text-blue-600 hover:text-blue-700 flex items-center gap-1">
              Alle <ArrowRight size={10} />
            </Link>
          </div>
          <div className="divide-y divide-border">
            {stats?.recent_companies?.length === 0 && (
              <div className="px-5 py-4 text-sm text-muted-foreground">Noch keine Firmen vorhanden</div>
            )}
            {stats?.recent_companies?.map(c => (
              <Link
                key={c.id}
                to={`/firmen/${c.id}`}
                data-testid={`recent-company-${c.id}`}
                className="flex items-center gap-3 px-5 py-3 hover:bg-muted/50 transition-colors"
              >
                <div className="w-7 h-7 rounded-sm bg-muted flex items-center justify-center text-xs font-bold text-foreground shrink-0">
                  {c.name[0].toUpperCase()}
                </div>
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-medium text-foreground truncate">{c.name}</p>
                  <p className="text-xs text-muted-foreground truncate">{c.industry || "—"}</p>
                </div>
                <span className={`text-xs px-1.5 py-0.5 rounded-sm font-medium ${c.status === "aktiv" ? "bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-400" : "bg-muted text-muted-foreground"}`}>
                  {c.status}
                </span>
              </Link>
            ))}
          </div>
        </div>

        {/* Recent Leads */}
        <div className="bg-card border border-border rounded-sm">
          <div className="flex items-center justify-between px-5 py-3.5 border-b border-border">
            <span className="text-xs font-semibold uppercase tracking-widest text-muted-foreground">Neueste Leads</span>
            <Link to="/leads" className="text-xs text-blue-600 hover:text-blue-700 flex items-center gap-1">
              Alle <ArrowRight size={10} />
            </Link>
          </div>
          <div className="divide-y divide-border">
            {stats?.recent_leads?.length === 0 && (
              <div className="px-5 py-4 text-sm text-muted-foreground">Noch keine Leads vorhanden</div>
            )}
            {stats?.recent_leads?.map(l => (
              <Link
                key={l.id}
                to="/leads"
                data-testid={`recent-lead-${l.id}`}
                className="flex items-center gap-3 px-5 py-3 hover:bg-muted/50 transition-colors"
              >
                <div className="w-7 h-7 rounded-sm bg-muted flex items-center justify-center text-xs font-bold text-foreground shrink-0">
                  {l.company_name[0]?.toUpperCase() || "?"}
                </div>
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-medium text-foreground truncate">{l.company_name}</p>
                  <p className="text-xs text-muted-foreground truncate">{l.contact_name || "—"}</p>
                </div>
                <span className={`text-xs px-1.5 py-0.5 rounded-sm font-medium ${STATUS_COLORS[l.status] || "bg-muted text-muted-foreground"}`}>
                  {l.status}
                </span>
              </Link>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}
